import { Loading } from '@/core/ui/components/Loading';

import { getErrorMessage, useSelectedUnit, useServices, useToast } from '@/app/providers';
import { Button } from '@/presentation/components/atoms/Button';
import { Input } from '@/presentation/components/atoms/Input';
import { useDateFormatter } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { AllocationInput, BankInfo, Charge, ChargeStatus, MemberChargeSummary, Payment, PaymentMethod, PaymentStatus, UploadPaymentProofDTO, User } from '@/shared/types';
import { AlertTriangle, CheckCircle, Clock, CreditCard, FileText, Info, Upload, XCircle } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { getUnitLabel } from '@/modules/identity/ui/components/UserMenuToggleV2';
import { useAbility } from '@/modules/identity/application/ability';

export const Payments = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const toast = useToast();
  const { formatDate } = useDateFormatter();
  const { chargeRepo, paymentRepo, userRepo, settingsRepo, fileRepo } = useServices();
  const { selectedUnitId, setSelectedUnitId, hasMultipleUnits } = useSelectedUnit();
  const canManagePayment = ability.can('manage', 'all') || ability.can('manage', 'Payment');
  const canCreatePayment = canManagePayment || ability.can('create', 'Payment');

  const [charges, setCharges] = useState<Charge[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<MemberChargeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [bankInfo, setBankInfo] = useState<BankInfo | null>(null);

  // Payment Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCharges, setSelectedCharges] = useState<Charge[]>([]);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [reference, setReference] = useState('');
  const [method, setMethod] = useState<PaymentMethod>(PaymentMethod.TRANSFER);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Todo (cargos, saldo, historial, cuenta) es de la propiedad seleccionada: al cambiarla
  // se recarga y solo la respuesta de la última selección pinta la pantalla.
  const loadSeq = useRef(0);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnitId]);

  // Cuenta efectiva del domicilio seleccionado: la de su sección o la del residencial.
  useEffect(() => {
    let cancelled = false;
    setBankInfo(null);
    settingsRepo
      .getBankInfo(selectedUnitId ?? undefined)
      .then((info) => { if (!cancelled) setBankInfo(info); })
      .catch(() => { if (!cancelled) setBankInfo(null); });
    return () => { cancelled = true; };
  }, [settingsRepo, selectedUnitId]);

  const init = async () => {
    const seq = ++loadSeq.current;
    const unitId = selectedUnitId ?? undefined;
    setLoading(true);
    setSelectedCharges([]); // los cargos de otra propiedad no se pagan desde esta
    try {
      const [myCharges, myPaymentsRes, mySummary, user] = await Promise.all([
        chargeRepo.getMyCharges(unitId),
        paymentRepo.getMyPayments({ limit: 10, unitId }),
        chargeRepo.getMySummary(unitId),
        userRepo.getCurrentUser(),
      ]);
      if (seq !== loadSeq.current) return; // ya se eligió otra propiedad

      setCharges(myCharges);
      setPayments(myPaymentsRes.data);
      setSummary(mySummary);
      setCurrentUser(user);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      console.error(e);
      toast.error(getErrorMessage(e));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  const pendingCharges = charges.filter(c => c.status === ChargeStatus.PENDING || c.status === ChargeStatus.PARTIAL);
  
  // Get IDs of charges that already have pending/verifying payments
  const chargesWithPendingPayments = new Set(
    payments
      .filter(p => p.status === PaymentStatus.PENDING || p.status === PaymentStatus.VERIFYING)
      .flatMap(p => p.allocations?.map(a => a.chargeId) || [])
  );
  
  // Filter out charges that already have pending payments
  const chargesToPay = pendingCharges.filter(c => !chargesWithPendingPayments.has(c.id));
  const totalDebt = summary?.totalOwed || 0;

  const handlePaySelected = () => {
    if (!canCreatePayment) return;
    if (selectedCharges.length === 0) {
      toast.warning('Selecciona al menos un cargo para pagar');
      return;
    }
    setIsModalOpen(true);
  };

  const handlePayAll = () => {
    if (!canCreatePayment) return;
    setSelectedCharges(chargesToPay);
    setIsModalOpen(true);
  };

  const toggleChargeSelection = (charge: Charge) => {
    if (!canCreatePayment) return;
    setSelectedCharges(prev => {
      const exists = prev.find(c => c.id === charge.id);
      if (exists) {
        return prev.filter(c => c.id !== charge.id);
      }
      return [...prev, charge];
    });
  };

  const totalSelected = selectedCharges.reduce((acc, c) => acc + c.balance, 0);

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreatePayment) return;

    if (!proofFile) {
      toast.warning('Debes adjuntar un comprobante de pago');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Subir el archivo primero
      const uploadedFile = await fileRepo.uploadFile(proofFile, 'payments');
      const proofUrl = uploadedFile.url;

      // 2. Crear las allocations
      const allocations: AllocationInput[] = selectedCharges.map(c => ({
        chargeId: c.id,
        amount: c.balance,
      }));

      // 3. Enviar el pago con la URL del archivo subido
      const data: UploadPaymentProofDTO = {
        amount: totalSelected,
        method,
        proofUrl,
        transactionReference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
        paidAt: new Date().toISOString(),
        allocations,
      };

      await paymentRepo.uploadProof(data);
      toast.success('Comprobante enviado. Sera verificado por administracion.');

      setIsModalOpen(false);
      setSelectedCharges([]);
      setProofFile(null);
      setReference('');
      setNotes('');
      init();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: ChargeStatus) => {
    const configs: Record<ChargeStatus, { icon: React.ReactNode; className: string; label: string }> = {
      [ChargeStatus.PENDING]: {
        icon: <Clock size={12}/>,
        className: 'bg-amber-50 text-amber-700 border-amber-200',
        label: 'Pendiente',
      },
      [ChargeStatus.PARTIAL]: {
        icon: <AlertTriangle size={12}/>,
        className: 'bg-blue-50 text-blue-700 border-blue-200',
        label: 'Parcial',
      },
      [ChargeStatus.PAID]: {
        icon: <CheckCircle size={12}/>,
        className: 'bg-green-50 text-green-700 border-green-200',
        label: 'Pagado',
      },
      [ChargeStatus.CANCELLED]: {
        icon: <XCircle size={12}/>,
        className: 'bg-slate-50 text-slate-500 border-slate-200',
        label: 'Cancelado',
      },
    };
    const config = configs[status];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: PaymentStatus) => {
    const configs: Record<PaymentStatus, { icon: React.ReactNode; className: string; label: string }> = {
      [PaymentStatus.PENDING]: {
        icon: <Clock size={12}/>,
        className: 'bg-slate-50 text-slate-600 border-slate-200',
        label: 'Pendiente',
      },
      [PaymentStatus.VERIFYING]: {
        icon: <AlertTriangle size={12}/>,
        className: 'bg-amber-50 text-amber-700 border-amber-200',
        label: 'En Verificacion',
      },
      [PaymentStatus.VERIFIED]: {
        icon: <CheckCircle size={12}/>,
        className: 'bg-green-50 text-green-700 border-green-200',
        label: 'Verificado',
      },
      [PaymentStatus.REJECTED]: {
        icon: <XCircle size={12}/>,
        className: 'bg-red-50 text-red-700 border-red-200',
        label: 'Rechazado',
      },
    };
    const config = configs[status];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  if (loading || !currentUser) return <Loading />;

  const unitOptions = currentUser.units ?? [];
  const activeUnit = unitOptions.find((assignment) => assignment.unitId === selectedUnitId)?.unit;
  const unitWithSection = (unit: NonNullable<typeof activeUnit>) =>
    unit.sectionName ? `${getUnitLabel(unit)} · ${unit.sectionName}` : getUnitLabel(unit);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-2xl font-bold text-slate-800">{t('payments.title')}</h2>
        {/* Cada propiedad tiene sus propios cargos, historial y cuenta (la de su sección). */}
        {hasMultipleUnits ? (
          <label className="text-sm text-slate-600 flex items-center gap-2">
            Propiedad
            <select
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500"
              value={selectedUnitId ?? ''}
              onChange={(e) => setSelectedUnitId(e.target.value)}
            >
              {unitOptions.map((assignment) => (
                <option key={assignment.unitId} value={assignment.unitId}>
                  {unitWithSection(assignment.unit)}
                </option>
              ))}
            </select>
          </label>
        ) : activeUnit ? (
          <span className="text-sm text-slate-500">{unitWithSection(activeUnit)}</span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">

          {/* Pending Charges */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-bold text-slate-800">{t('payments.pendingCharges')}</h3>
              {canCreatePayment && selectedCharges.length > 0 && (
                <Button onClick={handlePaySelected} icon={<Upload size={16}/>}>
                  {t('payments.paySelected')} (${totalSelected.toLocaleString()})
                </Button>
              )}
            </div>

            {chargesToPay.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <CheckCircle size={48} className="mx-auto mb-3 text-green-400"/>
                <p>{t('payments.noPendingCharges')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500 font-medium">
                    <tr>
                      {canCreatePayment && (
                        <th className="p-4 w-10">
                          <input
                            type="checkbox"
                            checked={selectedCharges.length === chargesToPay.length}
                            onChange={() => {
                              if (selectedCharges.length === chargesToPay.length) {
                                setSelectedCharges([]);
                              } else {
                                setSelectedCharges(chargesToPay);
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                        </th>
                      )}
                      <th className="p-4">{t('payments.concept')}</th>
                      <th className="p-4">{t('payments.dueDate')}</th>
                      <th className="p-4 text-right">{t('payments.amount')}</th>
                      <th className="p-4">{t('payments.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chargesToPay.map((charge) => (
                      <tr
                        key={charge.id}
                        className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${canCreatePayment ? 'cursor-pointer' : ''} ${
                          selectedCharges.find(c => c.id === charge.id) ? 'bg-primary-50' : ''
                        }`}
                        onClick={() => toggleChargeSelection(charge)}
                      >
                        {canCreatePayment && (
                          <td className="p-4">
                            <input
                              type="checkbox"
                              checked={!!selectedCharges.find(c => c.id === charge.id)}
                              onChange={() => toggleChargeSelection(charge)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 rounded border-slate-300"
                            />
                          </td>
                        )}
                        <td className="p-4">
                          <p className="font-medium text-slate-800">{charge.concept}</p>
                          {charge.period && (
                            <p className="text-xs text-slate-400">Periodo: {charge.period}</p>
                          )}
                        </td>
                        <td className="p-4">
                          <p className="text-slate-600">{formatDate(charge.dueDate)}</p>
                          {charge.isOverdue && (
                            <p className="text-xs text-red-600 font-bold">{charge.daysOverdue} {t('payments.overdue')}</p>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <p className="font-mono font-bold text-slate-800">${charge.balance.toLocaleString()}</p>
                          {charge.balance !== charge.amount && (
                            <p className="text-xs text-slate-400">{t('payments.de')}: ${charge.amount.toLocaleString()}</p>
                          )}
                        </td>
                        <td className="p-4">{getStatusBadge(charge.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Payments */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h3 className="font-bold text-slate-800">{t('payments.paymentHistory')}</h3>
            </div>

            {payments.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <FileText size={48} className="mx-auto mb-3 text-slate-300"/>
                <p>{t('payments.noPaymentHistory')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500 font-medium">
                    <tr>
                      <th className="p-4">{t('payments.date')}</th>
                      <th className="p-4">{t('payments.concept')}</th>
                      <th className="p-4 text-right">{t('payments.amount')}</th>
                      <th className="p-4">{t('payments.method')}</th>
                      <th className="p-4">{t('payments.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => {
                      const concept = payment.allocations?.length > 0 
                        ? payment.allocations.map(a => a.chargeConcept || a.chargePeriod).filter(Boolean).join(', ')
                        : 'Pago de cargos';
                      return (
                        <tr key={payment.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="p-4 text-slate-600">
                            {formatDate(payment.paidAt)}
                          </td>
                          <td className="p-4">
                            <div className="text-slate-800 font-medium">
                              {concept}
                            </div>
                          </td>
                          <td className="p-4 text-right font-mono font-bold text-slate-800">
                            ${payment.amount.toLocaleString()}
                          </td>
                          <td className="p-4 text-slate-600">
                            {payment.method === PaymentMethod.TRANSFER ? t('payments.transfer') :
                             payment.method === PaymentMethod.CASH ? t('payments.cash') :
                             payment.method === PaymentMethod.CARD ? t('payments.card') :
                             payment.method === PaymentMethod.CHECK ? t('payments.check') : t('payments.na')}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {getPaymentStatusBadge(payment.status)}
                              {payment.status === PaymentStatus.REJECTED && payment.rejectionReason && (
                                <div className="relative group">
                                  <Info size={14} className="text-red-500 cursor-help" />
                                  <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 z-50 hidden group-hover:block w-64 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-lg">
                                    <p className="font-semibold mb-1">{t('payments.rejectionReason')}:</p>
                                    <p>{payment.rejectionReason}</p>
                                    <div className="absolute bottom-full top-1/2 -translate-y-1/2 border-8 border-transparent border-l-slate-800" />
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
          {/* Balance Card */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 text-white shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-5 rounded-full -mr-10 -mt-10 blur-2xl"></div>

            <p className="text-slate-400 text-sm mb-1 font-medium">{t('payments.balance')}</p>
            <h3 className="text-4xl font-bold mb-6 tracking-tight">
              ${totalDebt.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              <span className="text-sm font-normal text-slate-400">MXN</span>
            </h3>

            {summary && (
              <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">{t('payments.pending')}</p>
                  <p className="font-bold">${(summary.pendingAmount ?? 0).toLocaleString()}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">{t('payments.overdue')}</p>
                  <p className="font-bold text-red-400">${(summary.overdueAmount ?? 0).toLocaleString()}</p>
                </div>
              </div>
            )}

            {canCreatePayment && (
              <button
                onClick={handlePayAll}
                disabled={chargesToPay.length === 0}
                className="w-full bg-primary-500 hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3.5 rounded-lg font-bold transition-all flex items-center justify-center gap-2 mb-3 shadow-lg shadow-primary-500/20"
              >
                <CreditCard size={18} />
                {t('payments.pay_total')}
              </button>
            )}

            <p className="text-xs text-center text-slate-500 mt-4">
              {t('payments.uploadReceipt')}
            </p>
          </div>

          {/* Bank Info Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
            <h3 className="font-bold text-slate-800 mb-1 text-sm uppercase tracking-wider">{t('payments.bankInfo')}</h3>
            <p className="text-xs text-slate-500 mb-4">
              {!bankInfo
                ? 'Cargando cuenta...'
                : bankInfo.source === 'SECTION'
                  ? `Cuenta de ${activeUnit?.sectionName ?? 'tu sección'}`
                  : bankInfo.source === 'NONE'
                    ? 'Tu residencial aún no tiene cuenta bancaria configurada. Consulta con administración.'
                    : 'Cuenta general del residencial'}
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between pb-2 border-b border-slate-50">
                <span className="text-slate-500">{t('payments.bank')}</span>
                <span className="font-medium text-slate-800">{bankInfo?.bankName || 'N/A'}</span>
              </div>
              <div className="flex justify-between pb-2 border-b border-slate-50">
                <span className="text-slate-500">{t('payments.beneficiary')}</span>
                <span className="font-medium text-slate-800">{bankInfo?.accountName || 'N/A'}</span>
              </div>
              <div>
                <span className="text-slate-500 block mb-1.5">{t('payments.clabe')}: {currentUser.residenceInfo?.unit}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono bg-slate-50 px-2 py-1.5 rounded text-slate-700 border border-slate-200 w-full text-center select-all">
                    {bankInfo?.clabe || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Upload Modal */}
      {isModalOpen && canCreatePayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                <Upload size={20} className="text-primary-500"/>
                {t('payments.uploadReceipt')}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle size={20}/>
              </button>
            </div>

            <form onSubmit={handleSubmitPayment} className="p-6 space-y-4">
              {/* Summary of selected charges */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400 uppercase font-bold mb-2">{t('payments.chargesToPay')}</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {selectedCharges.map(c => (
                    <div key={c.id} className="flex justify-between text-sm">
                      <span className="text-slate-600 truncate">{c.concept}</span>
                      <span className="font-mono text-slate-800">${c.balance.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between">
                  <span className="font-bold text-slate-800">{t('payments.total')}</span>
                  <span className="text-xl font-bold text-primary-600">${totalSelected.toLocaleString()}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('payments.paymentMethod')}</label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                >
                  <option value={PaymentMethod.TRANSFER}>{t('payments.transfer')}</option>
                  <option value={PaymentMethod.CASH}>{t('payments.cash')}</option>
                  <option value={PaymentMethod.CHECK}>{t('payments.check')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('payments.paymentReceipt')} *
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="proof-file-input"
                  />
                  <label
                    htmlFor="proof-file-input"
                    className="flex items-center justify-center gap-2 w-full rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
                  >
                    <Upload size={20} className="text-slate-400"/>
                    <span className="text-sm text-slate-600">
                      {proofFile ? proofFile.name : t('payments.selectFile')}
                    </span>
                  </label>
                </div>
                {proofFile && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle size={12}/>
                    {t('payments.selectedFile')}: {(proofFile.size / 1024).toFixed(1)} KB
                  </p>
                )}
              </div>

              <Input
                label={t('payments.reference') + ' / ' + t('payments.folio')}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder={t('payments.referencePlaceholder')}
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('payments.notes')} (opcional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('payments.notesPlaceholder')}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm min-h-[60px]"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setIsModalOpen(false)}
                >
                  {t('payments.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  isLoading={submitting}
                  icon={<Upload size={16}/>}
                >
                  {t('payments.sendProof')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
